import moment from 'moment';

import { checkinApi } from '@/services/api/httpClient';

import { allowedAdmissionStatus } from '@/utils/constants/admission.constants';
import { addDynatraceIdentifier } from '@/utils/helpers/addDynatraceIdentifier';
import { LOCAL_STORAGE_CPF } from '@/utils/constants/checkin.constants';

const updateAdmissionActions = {
  async getAdmissionAndMdmIdsByCpf(
    { commit, getters, dispatch },
    { cpf = '', token = '', retries = 3 },
  ) {
    const { unitId } = getters.getBrandUnitIdVariables;
    const date = new Date();
    const today = date.toISOString().split('T')[0];
    const initialDateDay = today;
    const finalDateDay = token ? moment(today).add(2, 'days').format('YYYY-MM-DD') : today;

    commit('enableLoader', true);

    const ids = { mdmPatientId: null, admissionData: null };
    const fieldErrors = { mdmError: null, admissionError: null };

    addDynatraceIdentifier(getters.cpfHidden);

    try {
      if (!unitId) {
        fieldErrors.admissionError = 'Unit id is null';
        return { data: ids, errors: fieldErrors };
      }

      const mdmIdsUrl = '/patient/mdmIds/cpf/' + cpf;
      const mdmIds = await checkinApi().get(mdmIdsUrl);

      if (!mdmIds?.data || !mdmIds?.data?.length) {
        fieldErrors.mdmError = { status: '404', message: 'MDM data not founded' };
        return { data: ids, errors: fieldErrors };
      }

      ids.mdmPatientId = mdmIds.data[0];
      commit('SET_MDM_ID_PATIENT', mdmIds.data[0]);
      addDynatraceIdentifier(getters.cpfHidden, mdmIds.data[0]);

      const schedulingsUrl = `/schedulings?mdmIds=${mdmIds.data.toString()}&admissionInitDate=${initialDateDay}T00:00:00-03:00&admissionFinishDate=${finalDateDay}T23:59:59-03:00&unitId=${unitId}`;
      const schedulings = await checkinApi().get(schedulingsUrl);

      let admissionData = null;

      if (schedulings?.data?.admissions?.content?.length) {
        admissionData = schedulings.data.admissions.content;

        const cpf = sessionStorage.getItem(LOCAL_STORAGE_CPF);
        if (token) {
          admissionData = admissionData.find((item) => item.id.split('-')[0] === token);

          if (!admissionData) {
            fieldErrors.admissionError = { status: '404', message: 'No valid admissionId founded' };
            return { data: ids, errors: fieldErrors };
          }

          ids.admissionData = admissionData;

          await getPatientAuthorization(cpf, admissionData.id);
        } else {
          admissionData = admissionData ? getMainAdmission(admissionData) : null;
          if (!admissionData) {
            fieldErrors.admissionError = { status: '404', message: 'Admission not founded' };
            return { data: ids, errors: fieldErrors };
          }

          ids.admissionData = admissionData;

          // @TODO: rever a necessidade de fazer essa chamada
          // Faz sentido chamar authorization para admissoes com esses status
          // se é um fluxo não agendado?
          if (
            admissionData.status === 'ADMISSION_DONE' ||
            admissionData.status === 'CHECK_IN_DONE'
          ) {
            await getPatientAuthorization(cpf, admissionData.id);
          }
        }

        commit('SET_ADMISSION_DATA', { ...admissionData });
        commit('SET_ADMISSION_STATUS', admissionData?.status);
        commit('SET_ADMISSION_ID', admissionData?.id);

        addDynatraceIdentifier(getters.cpfHidden, mdmIds.data[0], admissionData?.id);

        ids.admissionDataId = admissionData?.id;
      }
      return { data: ids, errors: fieldErrors };
    } catch (error) {
      if (error?.response?.status === 404) {
        fieldErrors.mdmError = { status: '404', message: 'MDM data not founded' };
        return { data: ids, errors: fieldErrors };
      }
      if (retries > 0) {
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            dispatch('getAdmissionAndMdmIdsByCpf', {
              cpf,
              token,
              retries: retries - 1,
            }).then(resolve, reject);
          }, 150);
        });
      }
    } finally {
      commit('enableLoader', false);
    }
  },

  async getAdmissionById({ state, commit }) {
    const admissionId = state.data.id;
    if (admissionId) {
      const url = `/admission/${admissionId}`;
      try {
        const admissionResponse = await checkinApi().get(url);

        if (admissionResponse && admissionResponse.data) {
          const {
            status,
            source: { origin },
          } = admissionResponse.data;
          commit('SET_ADMISSION_DATA', admissionResponse.data);
          commit('SET_ADMISSION_STATUS', status);

          if (origin !== 'AUT') {
            commit('global/SET_IS_SCHEDULED_ADMISSION', true);
          }

          return admissionResponse.data;
        }
      } catch (error) {
        console.log(error);
      }
    }
  },

  async updateAdmissionStatus({ getters, commit }, newStatus) {
    const url = `/admission/${getters.getCurrentAdmissionState.data.id}/${newStatus}`;
    try {
      const content = await checkinApi().patch(url);
      commit('SET_ADMISSION_STATUS', content?.data?.status);
    } catch (err) {
      console.log(err);
    }
  },

  async createAdmission({ commit }, params) {
    commit('enableLoader', true);

    const url = `/admission/create`;
    try {
      const content = await checkinApi().post(url, params);

      let admissionCreated = await pullingVisit(content.data, 0);

      if (admissionCreated) {
        admissionCreated = { ...admissionCreated, id: admissionCreated.admissionId };
        delete admissionCreated.admissionId;
        commit('SET_ADMISSION_DATA', admissionCreated);
        localStorage.setItem('admissionId', admissionCreated.id);
      }

      commit('enableLoader', false);
      return admissionCreated;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
};

export default updateAdmissionActions;

const getMainAdmission = (admissions) => {
  const admissionsByPriority = admissions.map((admission) => {
    const newAdmission = { ...admission };
    allowedAdmissionStatus.forEach((item) => {
      if (item.status === newAdmission.status) {
        newAdmission.priority = item.priority;
      }
    });

    return newAdmission;
  });

  const mainAdmission = admissionsByPriority.sort((item, nextItem) => {
    return item.priority - nextItem.priority;
  })[0];

  return mainAdmission;
};

async function pullingVisit(admission, tryNumber) {
  const vueAppMaxTryNumberPullingVisit = process.env.vueAppMaxTryNumberPullingVisit || 3;
  const vueAppSecondsDelayPullingVisit = process.env.vueAppSecondsDelayPullingVisit || 1;
  if (tryNumber >= vueAppMaxTryNumberPullingVisit) {
    return null;
  }

  let ret = { ...admission };

  await new Promise((resolve) => setTimeout(resolve, vueAppSecondsDelayPullingVisit * 1000));
  const url = `/admission/${admission.admissionId}/base`;
  const content = await checkinApi().get(url);

  if (content?.data?.visit) {
    ret.visit = content?.data?.visit;
    ret.source = {
      origin: content?.data?.origin,
    };
  } else {
    ret = await pullingVisit(admission, ++tryNumber);
  }

  return ret;
}

const getPatientAuthorization = async (cpf, admissionId) => {
  try {
    const response = await checkinApi().get(`/patient/authorization/${cpf}/${admissionId}`);

    if (response.data === '') {
      return null;
    }

    sessionStorage.setItem('user', response?.data);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 401) {
      console.error('Unauthorized cpf access to this admission');
      return null;
    }
    throw error;
  }
};
